import { useMutation } from '@apollo/client';
import { UpdateUserTabPreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateUserTabPreferencesTRPCInput } from './useUpdateUserTabPreferencesTRPC';
import { useUpdateUserTabPreferencesTRPC } from './useUpdateUserTabPreferencesTRPC';

type MutateArgs = { variables: UpdateUserTabPreferencesTRPCInput };

/**
 * Flag-gated wrapper for the `updateUserTabPreferences` mutation. Defaults
 * to the legacy Apollo mutation and delegates to the tRPC variant when the
 * `trpc` feature flag is enabled.
 *
 * Returns an Apollo-compatible tuple `[mutate]` so consumers can keep the
 * `[mutate] = useMutation(...)` / `await mutate({ variables })` shape.
 */
export const useUpdateUserTabPreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateUserTabPreferencesGraphQL] = useMutation(
    UpdateUserTabPreferencesDocument,
    {
      update: (cache) => {
        evictField(cache, 'user_tab_preference');
      },
    }
  );

  const trpcMutation = useUpdateUserTabPreferencesTRPC();

  const mutate = async ({ variables }: MutateArgs) => {
    if (trpcEnabled) {
      return trpcMutation.updateUserTabPreferences(variables);
    }

    const result = await updateUserTabPreferencesGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update user tab preferences');
    }

    return result.data;
  };

  return [mutate] as const;
};
